import * as React from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * ContribuyenteCombobox — Selector de contribuyente con barra de búsqueda que FUNCIONA en Android.
 *
 * Motivación: el patrón anterior usaba `<Select>` de Radix con un `<Input>` embebido dentro
 * de `<SelectContent>`. Radix Select NO soporta inputs internos (es un dropdown cerrado tipo
 * <select> nativo). En Android específicamente el keyboard virtual + focus traps de Radix
 * causaban que no se pudiera escribir. Aplicar `stopPropagation` en 12 eventos no lo arreglaba
 * porque Radix atrapa touch events a nivel de portal, antes que bubbling.
 *
 * Solución: `Popover + Command (cmdk)` — el patrón oficial de shadcn/ui para combobox con
 * búsqueda. Funciona nativamente en mobile (Android + iOS) sin hacks.
 *
 * Props:
 *  - value: string  — id del contribuyente actualmente seleccionado.
 *  - onChange: (id: string, contribuyente: object|null) => void
 *  - contribuyentes: Array<{id, tipo_documento, documento, razon_social, nombre, apellido, numero_lae, codigo_catastral}>
 *  - search?: string (controlado — sincroniza con el store del padre si aplica).
 *  - onSearchChange?: (str) => void
 *  - placeholder?: string
 *  - emptyText?: string
 *  - disabled?: boolean
 *  - testId?: string       — data-testid del trigger.
 *  - triggerClassName?: string
 */
export function ContribuyenteCombobox({
  value,
  onChange,
  contribuyentes = [],
  search,
  onSearchChange,
  placeholder = "Seleccione contribuyente",
  emptyText = "Sin coincidencias",
  disabled = false,
  testId,
  triggerClassName,
}) {
  const [open, setOpen] = React.useState(false);
  const [localSearch, setLocalSearch] = React.useState("");
  const currentSearch = search !== undefined ? search : localSearch;
  const setSearch = onSearchChange ?? setLocalSearch;

  const seleccionado = React.useMemo(
    () => contribuyentes.find((c) => c.id === value) || null,
    [contribuyentes, value],
  );

  const displayLabel = React.useMemo(() => {
    if (!seleccionado) return placeholder;
    const doc = [seleccionado.tipo_documento, seleccionado.documento]
      .filter(Boolean).join("-");
    const nombre = seleccionado.razon_social
      || [seleccionado.nombre, seleccionado.apellido].filter(Boolean).join(" ");
    return doc ? `${doc} · ${nombre}` : nombre;
  }, [seleccionado, placeholder]);

  const normalize = (s) =>
    String(s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full justify-between font-normal bg-white",
            !seleccionado && "text-slate-500",
            triggerClassName,
          )}
        >
          <span className="truncate text-left">{displayLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        sideOffset={4}
      >
        <Command
          shouldFilter={false}  // Filtrado manual para performance con listas grandes
        >
          <CommandInput
            value={currentSearch}
            onValueChange={setSearch}
            placeholder="Buscar por RIF/Cédula o nombre…"
            data-testid={testId ? `${testId}-search` : undefined}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
          <CommandList className="max-h-64">
            {contribuyentes.length === 0 ? (
              <CommandEmpty>Sin contribuyentes registrados</CommandEmpty>
            ) : (
              (() => {
                const q = normalize(currentSearch.trim());
                const filtrados = !q ? contribuyentes : contribuyentes.filter((c) => {
                  const haystack = normalize([
                    c.tipo_documento, c.documento,
                    `${c.tipo_documento || ""}-${c.documento || ""}`,
                    c.razon_social, c.nombre, c.apellido,
                    c.numero_lae, c.codigo_catastral,
                  ].filter(Boolean).join(" "));
                  return haystack.includes(q);
                });
                if (filtrados.length === 0) return <CommandEmpty>{emptyText}</CommandEmpty>;
                return (
                  <CommandGroup>
                    {filtrados.map((c) => {
                      const doc = [c.tipo_documento, c.documento].filter(Boolean).join("-");
                      const nombre = c.razon_social
                        || [c.nombre, c.apellido].filter(Boolean).join(" ");
                      const label = doc ? `${doc} · ${nombre}` : nombre;
                      return (
                        <CommandItem
                          key={c.id}
                          value={c.id}
                          onSelect={() => {
                            onChange(c.id, c);
                            setOpen(false);
                          }}
                          data-testid={testId ? `${testId}-opt-${c.id}` : undefined}
                          className="cursor-pointer"
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              value === c.id ? "opacity-100" : "opacity-0",
                            )}
                          />
                          <span className="truncate">{label}</span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                );
              })()
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default ContribuyenteCombobox;
